import React from 'react';
import {Table, UncontrolledTooltip, Pagination, PaginationItem, PaginationLink} from "reactstrap";
import Moment from 'moment';

const sortTypes = {
	up: {
		class: 'sort-up',
	},
	down: {
		class: 'sort-down',
	},
	default: {
		class: 'sort',
	}
};

// Convert snake_case to Title Case
const toTitleCase = (str) => {
	return str.split('_').map(word =>
		word.charAt(0).toUpperCase() + word.slice(1)
	).join(' ');
};

// Extract all unique rating keys from data
const getRatingKeys = (data) => {
	const keys = new Set();
	data.forEach(row => {
		if (row.scoring?.ratings) {
			Object.keys(row.scoring.ratings).forEach(k => keys.add(k));
		}
	});
	return Array.from(keys);
};

// Safe accessor for score
const getScore = (row, key) => {
	return row.scoring?.ratings?.[key]?.score ?? '-';
};

// Safe accessor for notes
const getNotes = (row, key) => {
	return row.scoring?.ratings?.[key]?.notes ?? '';
};

class VoiceAnalyzeTable extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			currentSort: 'default',
			sortField: ''
		};
	}

	onSortChange = (field) => {
		const { currentSort } = this.state;
		let nextSort;

		if (currentSort === 'down') nextSort = 'up';
		else if (currentSort === 'up') nextSort = 'default';
		else if (currentSort === 'default') nextSort = 'down';

		this.setState({
			currentSort: nextSort,
			sortField: field
		});
	};

	processData = () => {
		const {sortField, currentSort} = this.state;
		let ret = [...this.props.data];

		if (sortField === '' || currentSort === 'default') {
			return ret;
		}

		ret.sort((a, b) => {
			let aVal, bVal;
			if (sortField === 'deal_id') {
				aVal = a.deal_id ?? 0;
				bVal = b.deal_id ?? 0;
			} else if (sortField === 'processed_time') {
				aVal = a.processed_time ?? '';
				bVal = b.processed_time ?? '';
			} else {
				aVal = a[sortField] ?? '';
				bVal = b[sortField] ?? '';
			}

			if (currentSort === 'up') {
				return aVal < bVal ? -1 : (aVal > bVal ? 1 : 0);
			} else {
				return aVal < bVal ? 1 : (aVal > bVal ? -1 : 0);
			}
		});

		return ret;
	};

	renderSortIcon = (field) => {
		const { currentSort, sortField } = this.state;
		if (sortField === field) {
			return <i className={`fas fa-${sortTypes[currentSort].class}`} />;
		}
		return <i className={`fas fa-${sortTypes['default'].class}`} />;
	};

	truncateText = (text, maxLength = 100) => {
		if (!text) return '-';
		if (text.length <= maxLength) return text;
		return text.substring(0, maxLength) + '...';
	};

	renderTrainingOpportunities = (opportunities) => {
		if (!opportunities || !Array.isArray(opportunities) || opportunities.length === 0) {
			return '-';
		}
		return opportunities.join(', ');
	};

	render() {
		const { sortField } = this.state;
		const { page, totalCount, pageSize, onPageChange } = this.props;
		const data = this.processData();
		const ratingKeys = getRatingKeys(data);
		const totalPages = Math.ceil(totalCount / pageSize);

		return (
			<>
				<Table className="tablesorter" responsive>
					<thead className="text-primary">
						<tr>
							<th
								className="sortable"
								onClick={() => this.onSortChange('deal_id')}>
								Deal ID &nbsp;
								{this.renderSortIcon('deal_id')}
							</th>
							<th
								className="sortable"
								onClick={() => this.onSortChange('processed_time')}>
								Processed Time &nbsp;
								{this.renderSortIcon('processed_time')}
							</th>
							{ratingKeys.map(key => (
								<th key={key} className="text-center">
									{toTitleCase(key)}
								</th>
							))}
							<th>Additional Flags</th>
							<th>Call Summary</th>
							<th>Training Opportunities</th>
						</tr>
					</thead>
					<tbody>
						{data.map(row => (
							<tr key={row.id}>
								<td>{row.deal_id ?? '-'}</td>
								<td>
									{row.processed_time
										? Moment(row.processed_time).format('YYYY-MM-DD HH:mm')
										: '-'}
								</td>
								{ratingKeys.map(key => {
									const score = getScore(row, key);
									const notes = getNotes(row, key);
									const tooltipId = `score-${row.id}-${key.replace(/[^a-zA-Z0-9]/g, '')}`;
									return (
										<td key={key} className="text-center" id={tooltipId}>
											{score}
											{notes && (
												<UncontrolledTooltip target={tooltipId} placement="top">
													{notes}
												</UncontrolledTooltip>
											)}
										</td>
									);
								})}
								<td>
									{row.scoring?.call_outcome?.additional_flags ?? '-'}
								</td>
								<td id={`summary-${row.id}`}>
									{this.truncateText(row.scoring?.call_summary)}
									{row.scoring?.call_summary && row.scoring.call_summary.length > 100 && (
										<UncontrolledTooltip target={`summary-${row.id}`} placement="top">
											{row.scoring.call_summary}
										</UncontrolledTooltip>
									)}
								</td>
								<td>
									{this.renderTrainingOpportunities(row.scoring?.training_opportunities)}
								</td>
							</tr>
						))}
					</tbody>
				</Table>

				{totalPages > 1 && (
					<Pagination className="pagination justify-content-center">
						<PaginationItem disabled={page === 1}>
							<PaginationLink previous onClick={() => onPageChange(page - 1)} />
						</PaginationItem>
						<PaginationItem>
							<PaginationLink disabled>
								{page} of {totalPages}
							</PaginationLink>
						</PaginationItem>
						<PaginationItem disabled={page >= totalPages}>
							<PaginationLink next onClick={() => onPageChange(page + 1)} />
						</PaginationItem>
					</Pagination>
				)}
			</>
		);
	}
}

export default VoiceAnalyzeTable;
